if (!customElements.get("metafield-image-slider")) {
  class MetafieldImageSlider extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.swiper = null;
      this.observer = null;
      this.initialized = false;
      this.zoomMediaQuery = window.matchMedia("(min-width: 750px)");
      this.handleZoomMediaChange = this.updateZoomButtons.bind(this);
    }

    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.slider = this.querySelector(".swiper");
      this.updateZoomButtons();
      this.addZoomMediaListener();

      if (!this.slider) {
        return;
      }

      const slides = this.querySelectorAll(".swiper-slide");

      if (slides.length < 2) {
        this.classList.add("is-static");
        return;
      }

      const shouldLazyInit = this.dataset.lazyInit === "true";

      if (shouldLazyInit && "IntersectionObserver" in window && !this.isThemeEditor()) {
        this.observe();
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.removeZoomMediaListener();
      this.initialized = false;
    }

    observe() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            this.observer.disconnect();
            this.observer = null;
            this.init();
          });
        },
        {
          rootMargin: "0px",
          threshold: 0.01,
        },
      );

      this.observer.observe(this);
    }

    async init() {
      if (this.initialized) {
        return;
      }

      try {
        await this.loadSwiper();

        if (!window.Swiper) {
          console.warn("[metafield-image-slider] Swiper is not available.");
          return;
        }

        const options = this.getOptions();

        this.swiper = new window.Swiper(this.slider, options);
        this.initialized = true;
        this.classList.add("is-initialized");
      } catch (error) {
        console.warn("[metafield-image-slider] Failed to initialize slider:", error);
      }
    }

    getOptions() {
      let options = {};

      try {
        options = JSON.parse(this.dataset.swiperOptions || "{}");
      } catch (error) {
        console.warn("[metafield-image-slider] Invalid Swiper options JSON:", error);
      }

      if (options.navigation) {
        options.navigation = {
          nextEl: this.querySelector("[data-slider-next]"),
          prevEl: this.querySelector("[data-slider-prev]"),
        };
      } else {
        delete options.navigation;
      }

      if (options.pagination) {
        options.pagination = {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
        };
      } else {
        delete options.pagination;
      }

      if (!options.autoplay) {
        delete options.autoplay;
      }

      options.resizeObserver = true;
      options.observeParents = true;

      return options;
    }

    async loadSwiper() {
      if (window.Swiper) {
        return;
      }

      if (typeof window.loadSwiper === "function") {
        await window.loadSwiper();
        return;
      }

      if (window.__metafieldImageSliderSwiperPromise) {
        return window.__metafieldImageSliderSwiperPromise;
      }

      const jsUrl = this.dataset.swiperJs;
      const cssUrl = this.dataset.swiperCss;

      window.__metafieldImageSliderSwiperPromise = Promise.all([this.loadStylesheet(cssUrl), this.loadScript(jsUrl)]);

      return window.__metafieldImageSliderSwiperPromise;
    }

    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (!src) {
          reject(new Error("Missing Swiper JS asset URL."));
          return;
        }

        const existingScript = document.querySelector(`script[src="${src}"]`);

        if (existingScript) {
          if (existingScript.dataset.loaded === "true") {
            resolve();
            return;
          }

          existingScript.addEventListener("load", () => resolve(), {once: true});
          existingScript.addEventListener("error", reject, {once: true});
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;

        script.addEventListener(
          "load",
          () => {
            script.dataset.loaded = "true";
            resolve();
          },
          {once: true},
        );

        script.addEventListener("error", reject, {once: true});

        document.head.appendChild(script);
      });
    }

    loadStylesheet(href) {
      return new Promise((resolve) => {
        if (!href) {
          resolve();
          return;
        }

        const existingLink = document.querySelector(`link[href="${href}"]`);

        if (existingLink) {
          resolve();
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => resolve();
        link.onerror = () => resolve();

        document.head.appendChild(link);
      });
    }

    isThemeEditor() {
      return window.Shopify && window.Shopify.designMode;
    }

    isZoomEnabled() {
      if (this.zoomMediaQuery.matches) {
        return this.classList.contains("mis--zoom-desktop");
      }

      return this.classList.contains("mis--zoom-mobile");
    }

    updateZoomButtons() {
      const isEnabled = this.isZoomEnabled();
      const buttons = this.querySelectorAll(".mis__media-button");

      buttons.forEach((button) => {
        button.disabled = !isEnabled;
        button.setAttribute("aria-disabled", String(!isEnabled));
      });
    }

    addZoomMediaListener() {
      if (typeof this.zoomMediaQuery.addEventListener === "function") {
        this.zoomMediaQuery.addEventListener("change", this.handleZoomMediaChange);
        return;
      }

      this.zoomMediaQuery.addListener(this.handleZoomMediaChange);
    }

    removeZoomMediaListener() {
      if (typeof this.zoomMediaQuery.removeEventListener === "function") {
        this.zoomMediaQuery.removeEventListener("change", this.handleZoomMediaChange);
        return;
      }

      this.zoomMediaQuery.removeListener(this.handleZoomMediaChange);
    }
  }

  customElements.define("metafield-image-slider", MetafieldImageSlider);
}
